import React, { useState, useEffect } from 'react';
import { 
  Compass, Search, Zap, AlertTriangle, TrendingUp, Calculator, 
  DollarSign, Award, Info, ExternalLink, Globe, ArrowLeft, 
  X, ChevronDown, BookOpen, MessageCircle, BarChart3, 
  HelpCircle, CheckCircle2, ChevronRight
} from 'lucide-react';

const NETWORK_URLS = {
  ClickBank: 'https://accounts.clickbank.com',
  JVZoo: 'https://www.jvzoo.com/affiliate',
  MaxBounty: 'https://www.maxbounty.com',
  Digistore24: 'https://www.digistore24.com/affiliate',
  CPALead: 'https://www.cpalead.com'
};

// Pre-defined database of trending offers for common niches to ensure realistic first-impressions
const PREDEFINED_OFFERS = {
  "weight loss": [
    {
      id: "cb_alpilean",
      name: "Alpilean Ice Hack Supplement",
      network: "ClickBank",
      payout: 147.00,
      payoutType: "CPS",
      commission: 75,
      geo: "US, CA, UK, AU",
      gravity: 142.5,
      epc: 2.10,
      metaAdsActive: 18,
      category: "Health & Fitness",
      description: "Alpilean is a natural dietary supplement designed to target low inner body temperature, a newly discovered common factor in unexplained weight gain.",
      trafficSources: [
        { name: "NATIVE ADS (Taboola / MGID)", status: "RECOMMENDED", cpc: "$0.25–$0.45", why: "Health supplements and weight loss angles thrive on native editorial/advertorial layouts" },
        { name: "FACEBOOK / META", status: "WORTH TESTING", cpc: "$0.40–$0.80", why: "Mass appeal niche, but policy compliance is strict — avoid direct claims" },
        { name: "EMAIL / SOLO ADS", status: "WORTH TESTING", cpc: "$0.30–$0.50", why: "Weight loss list owners monetize well with clean bridge pages" },
        { name: "GOOGLE SEARCH", status: "PRICIER", cpc: "$1.50–$3.00", why: "High intent search CPCs are very competitive, bid carefully" }
      ],
      ads: [
        { pageName: "Healthy Ice Hack Reviews", duration: "18 days", cta: "Learn More", angle: "Unusual alpine secret melts 32lbs of stubborn fat" },
        { pageName: "Daily Wellness Tips", duration: "9 days", cta: "Order Now", angle: "Odd morning trick targets inner body temperature" },
        { pageName: "Alpilean Direct", duration: "4 days", cta: "Learn More", angle: "Top medical doctor reveals fat loss ice method" }
      ]
    },
    {
      id: "cb_puravive",
      name: "Puravive Exotic Rice Hack",
      network: "ClickBank",
      payout: 135.00,
      payoutType: "CPS",
      commission: 85,
      geo: "US, CA, UK, AU",
      gravity: 215.3,
      epc: 2.40,
      metaAdsActive: 32,
      category: "Health & Fitness",
      description: "A proprietary blend of 8 exotic nutrients and plants backed by clinical research to target root cause of belly fat.",
      trafficSources: [
        { name: "NATIVE ADS (Taboola / MGID)", status: "RECOMMENDED", cpc: "$0.25–$0.45", why: "High gravity offers convert extremely well with native advertorial pre-sells" },
        { name: "FACEBOOK / META", status: "WORTH TESTING", cpc: "$0.45–$0.90", why: "Scale potential is massive, but watch ad accounts closely due to high reports" },
        { name: "EMAIL / SOLO ADS", status: "WORTH TESTING", cpc: "$0.35–$0.60", why: "Works well with newsletter sponsorships and pre-coded templates" }
      ],
      ads: [
        { pageName: "Exotic Weight Loss Hacks", duration: "25 days", cta: "Learn More", angle: "Exotic rice recipe burns fat while you sleep" },
        { pageName: "Pure Nutrients", duration: "14 days", cta: "Order Now", angle: "8 exotic ingredients clinically proven to reduce weight" }
      ]
    },
    {
      id: "mb_keto_diet",
      name: "Custom Keto Diet Plan",
      network: "MaxBounty",
      payout: 45.00,
      payoutType: "CPA",
      commission: 100, // CPA is flat rate
      geo: "US, CA, UK",
      gravity: 45.2,
      epc: 0.95,
      metaAdsActive: 3,
      category: "Health & Fitness",
      description: "Personalized custom keto diet plan designed based on user input, weight, habits, and food preferences.",
      trafficSources: [
        { name: "FACEBOOK / META", status: "RECOMMENDED", cpc: "$0.30–$0.60", why: "Keto diet plans are very visual and have lower refund/complaint rates than pills" },
        { name: "GOOGLE SEARCH", status: "RECOMMENDED", cpc: "$0.80–$1.50", why: "High intent search for 'custom keto diet' converts exceptionally well" },
        { name: "NATIVE ADS (Taboola / MGID)", status: "WORTH TESTING", cpc: "$0.20–$0.35", why: "Lower payout requires highly engaging pre-sell to hit ROI" }
      ],
      ads: [
        { pageName: "Keto Recipes Hub", duration: "5 days", cta: "Sign Up", angle: "Take this 60-second quiz to get your custom keto meal plan" }
      ]
    },
    {
      id: "cb_javaburn",
      name: "Java Burn Coffee Additive",
      network: "ClickBank",
      payout: 110.00,
      payoutType: "CPS",
      commission: 80,
      geo: "US, CA, UK, AU",
      gravity: 154.2,
      epc: 1.90,
      metaAdsActive: 24,
      category: "Health & Fitness",
      description: "A tiny, tasteless morning coffee supplement that instantly boosts metabolism and targets stubborn fat cells.",
      trafficSources: [
        { name: "NATIVE ADS (Taboola / MGID)", status: "RECOMMENDED", cpc: "$0.25–$0.45", why: "Coffee supplement angle matches native content style perfectly" },
        { name: "FACEBOOK / META", status: "WORTH TESTING", cpc: "$0.40–$0.75", why: "Great CTR with coffee-themed creatives, but requires landing page disclaimers" },
        { name: "EMAIL / SOLO ADS", status: "WORTH TESTING", cpc: "$0.30–$0.50", why: "Easy sell to general self-improvement and health mailing lists" }
      ],
      ads: [
        { pageName: "Coffee Secrets", duration: "21 days", cta: "Learn More", angle: "Add this 1 teaspoon to your morning coffee to boost metabolism" },
        { pageName: "Daily Morning Hacks", duration: "12 days", cta: "Learn More", angle: "Taste-free powder converts ordinary coffee into fat-burning engine" }
      ]
    },
    {
      id: "cb_livpure",
      name: "Liv Pure Liver Purification",
      network: "ClickBank",
      payout: 142.00,
      payoutType: "CPS",
      commission: 70,
      geo: "US, CA, UK, AU, NZ",
      gravity: 98.4,
      epc: 1.85,
      metaAdsActive: 8,
      category: "Health & Fitness",
      description: "Liv Pure is a double-action formula designed to support liver function, optimize liver health, and accelerate fat burning.",
      trafficSources: [
        { name: "NATIVE ADS (Taboola / MGID)", status: "RECOMMENDED", cpc: "$0.30–$0.50", why: "Science-based liver optimization angles perform well on native news networks" },
        { name: "FACEBOOK / META", status: "WORTH TESTING", cpc: "$0.50–$0.90", why: "Harder to run directly on FB due to liver-related claims, use a bridge page" }
      ],
      ads: [
        { pageName: "Scientific Wellness", duration: "6 days", cta: "Learn More", angle: "Cardiologists discover real root cause of weight gain: liver health" }
      ]
    },
    {
      id: "cb_ikaria",
      name: "Ikaria Lean Belly Juice",
      network: "ClickBank",
      payout: 156.00,
      payoutType: "CPS",
      commission: 75,
      geo: "US, CA, UK, AU",
      gravity: 189.6,
      epc: 2.25,
      metaAdsActive: 27,
      category: "Health & Fitness",
      description: "Ikaria Lean Belly Juice is a potent powdered supplement formula based on the traditional diet of Ikaria island, designed to reduce uric acid and boost weight loss.",
      trafficSources: [
        { name: "NATIVE ADS (Taboola / MGID)", status: "RECOMMENDED", cpc: "$0.25–$0.45", why: "Fascinating story angle about the blue zone island of Ikaria drives massive native CTR" },
        { name: "FACEBOOK / META", status: "WORTH TESTING", cpc: "$0.40–$0.85", why: "High payout supports Facebook CPA, but keep copy focused on energy and longevity" },
        { name: "EMAIL / SOLO ADS", status: "WORTH TESTING", cpc: "$0.30–$0.55", why: "Blue zone juice recipe angle works well in standard swipe copies" }
      ],
      ads: [
        { pageName: "Longevity Secrets", duration: "16 days", cta: "Learn More", angle: "The ancient blue zone juice recipe that blocks fat absorption" },
        { pageName: "Ikaria Juice Official", duration: "11 days", cta: "Order Now", angle: "Dissolve 1 scoop of this delicious juice to target uric acid" }
      ]
    }
  ],
  "crypto": [
    {
      id: "cb_crypto_leap",
      name: "Crypto Quantum Leap",
      network: "ClickBank",
      payout: 240.00,
      payoutType: "CPS",
      commission: 50,
      geo: "Worldwide",
      gravity: 32.4,
      epc: 3.20,
      metaAdsActive: 4,
      category: "Finance",
      description: "Crypto Quantum Leap is a comprehensive online course showing beginners exactly how to profit from the current crypto cycle, from security to advanced trading strategies.",
      trafficSources: [
        { name: "EMAIL / SOLO ADS", status: "RECOMMENDED", cpc: "$0.40–$0.70", why: "Make Money Online (MMO) and investment email lists convert well on course promotions" },
        { name: "NATIVE ADS (Taboola / MGID)", status: "WORTH TESTING", cpc: "$0.35–$0.55", why: "Use financial editorial layouts focusing on 'The Bitcoin Halfing' or 'Retirement Protection'" },
        { name: "GOOGLE SEARCH", status: "PRICIER", cpc: "$1.80–$3.50", why: "Keywords are highly competitive, target long-tail reviews for best ROI" },
        { name: "FACEBOOK / META", status: "RESTRICTED", cpc: "-", why: "Extremely high restriction vertical. High chance of ad account bans for crypto courses" }
      ],
      ads: [
        { pageName: "Crypto Academy", duration: "7 days", cta: "Learn More", angle: "How this ex-banker made a fortune with crypto (Full guide for beginners)" }
      ]
    },
    {
      id: "cb_crypto_vip",
      name: "Intelligent Cryptocurrency VIP",
      network: "ClickBank",
      payout: 450.00,
      payoutType: "CPS",
      commission: 50,
      geo: "US, CA, UK, AU",
      gravity: 12.1,
      epc: 4.50,
      metaAdsActive: 2,
      category: "Finance",
      description: "Intelligent Cryptocurrency is a high-ticket membership community providing crypto research, education, trade alerts, and technical analysis guides.",
      trafficSources: [
        { name: "EMAIL / SOLO ADS", status: "RECOMMENDED", cpc: "$0.50–$0.90", why: "High payout supports premium solo ad traffic and newsletter buys" },
        { name: "GOOGLE SEARCH", status: "WORTH TESTING", cpc: "$2.00–$4.00", why: "High ticket conversions require brand reviews or specific educational queries" },
        { name: "FACEBOOK / META", status: "RESTRICTED", cpc: "-", why: "Policy risk is high. Do not attempt direct ads; use general investment/tech angles" }
      ],
      ads: [
        { pageName: "Premium Crypto Reports", duration: "10 days", cta: "Learn More", angle: "Top cryptocurrency researcher reveals his private portfolio for 2026" }
      ]
    },
    {
      id: "jv_cointip",
      name: "CoinTip Signals Membership",
      network: "JVZoo",
      payout: 79.00,
      payoutType: "Revshare",
      commission: 40,
      geo: "Worldwide",
      gravity: 18.5,
      epc: 1.40,
      metaAdsActive: 12,
      category: "Finance",
      description: "A monthly recurring subscription service sending real-time crypto trading signals, technical charts, and stop-loss advice to subscribers.",
      trafficSources: [
        { name: "EMAIL / SOLO ADS", status: "RECOMMENDED", cpc: "$0.30–$0.60", why: "Recurring offers are a favorite for list-builders who want passive affiliate commissions" },
        { name: "FACEBOOK / META", status: "RESTRICTED", cpc: "$0.50–$1.00", why: "Meta restricts trading signals. Must promote a free ebook or newsletter first" }
      ],
      ads: [
        { pageName: "Daily Trade Alerts", duration: "14 days", cta: "Sign Up", angle: "Get real-time cryptocurrency signals sent directly to your phone" }
      ]
    }
  ],
  "ai tools": [
    {
      id: "cust_jasper",
      name: "Jasper AI Affiliate Program",
      network: "Custom",
      payout: 99.00,
      payoutType: "Revshare",
      commission: 30,
      geo: "Worldwide",
      gravity: 85.0,
      epc: 2.10,
      metaAdsActive: 28,
      category: "Software & SaaS",
      description: "Jasper is an advanced AI copywriter and content generator that helps businesses automate their blogging, social media, and ad copy.",
      trafficSources: [
        { name: "GOOGLE SEARCH", status: "RECOMMENDED", cpc: "$0.80–$1.50", why: "High search intent for 'best AI copywriter' and 'Jasper AI reviews' makes this convert well" },
        { name: "FACEBOOK / META", status: "RECOMMENDED", cpc: "$0.45–$0.90", why: "Good targeting options for 'content marketers' and 'copywriters' with visually engaging ad copy demos" },
        { name: "EMAIL / SOLO ADS", status: "WORTH TESTING", cpc: "$0.35–$0.60", why: "Monetize digital marketing lists by showing how Jasper saves hours of work" }
      ],
      ads: [
        { pageName: "Digital Marketing Insider", duration: "30 days", cta: "Learn More", angle: "How I write 10,000 words a day with this smart AI copywriter" },
        { pageName: "SaaS Tools Review", duration: "15 days", cta: "Sign Up", angle: "Try the leading AI writing assistant for free and automate your blog posts" }
      ]
    },
    {
      id: "wp_copygenius",
      name: "CopyGenius AI Copywriter",
      network: "WarriorPlus",
      payout: 47.00,
      payoutType: "CPS",
      commission: 50,
      geo: "Worldwide",
      gravity: 24.5,
      epc: 0.85,
      metaAdsActive: 4,
      category: "Software & SaaS",
      description: "CopyGenius AI is a budget-friendly suite of AI tools designed for e-commerce stores, helping them generate product descriptions, ad copy, and email templates.",
      trafficSources: [
        { name: "FACEBOOK / META", status: "RECOMMENDED", cpc: "$0.30–$0.60", why: "Target Shopify store owners and e-commerce entrepreneurs with ad copy examples" },
        { name: "GOOGLE SEARCH", status: "RECOMMENDED", cpc: "$0.50–$1.00", why: "Lower CPC on terms like 'product description generator' helps secure conversions" }
      ],
      ads: [
        { pageName: "Ecom Growth Labs", duration: "8 days", cta: "Download", angle: "Generate high-converting Shopify product descriptions in 3 seconds" }
      ]
    }
  ]
};

const OfferScout = ({ openWizard, onBack }) => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Search Filters
  const [payoutFilter, setPayoutFilter] = useState('Any');
  const [typeFilter, setTypeFilter] = useState('Any');
  const [geoFilter, setGeoFilter] = useState('Any');
  
  // Sidebar Refine Filters (Separate so changes in Results update dynamically)
  const [refinePayout, setRefinePayout] = useState('Any');
  const [refineType, setRefineType] = useState('Any');
  const [refineGeo, setRefineGeo] = useState('Any');
  
  // Sorting & Results
  const [sortField, setSortField] = useState('score'); // 'score' | 'payout' | 'ads' | 'epc'
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  
  // Detail Modal Calculator State
  const [customCPC, setCustomCPC] = useState('');
  
  // Active Filter Dropdowns
  const [activeDropdown, setActiveDropdown] = useState(null); // 'payout' | 'type' | 'geo' | null

  // Procedural Generator (Creates highly realistic mock offers for any niche keyword)
  const generateOffers = (searchQuery) => {
    const term = searchQuery.trim().toLowerCase();
    
    // Check if we have pre-coded results first
    let baseOffers = [];
    if (PREDEFINED_OFFERS[term]) {
      baseOffers = JSON.parse(JSON.stringify(PREDEFINED_OFFERS[term]));
    } else {
      // Find partial matches in predefined data
      const matchingKeys = Object.keys(PREDEFINED_OFFERS).filter(k => term.includes(k) || k.includes(term));
      if (matchingKeys.length > 0) {
        baseOffers = JSON.parse(JSON.stringify(PREDEFINED_OFFERS[matchingKeys[0]]));
      } else {
        // Run procedural generator
        baseOffers = generateProceduralOffers(searchQuery);
      }
    }
    
    return baseOffers;
  };

  // Generate offers procedurally when keyword isn't predefined
  const generateProceduralOffers = (nicheQuery) => {
    const cleaned = nicheQuery.trim();
    const words = cleaned.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    const primaryWord = words[0] || 'Niche';
    const secondaryWord = words[1] || '';
    
    const niches = [
      { keywords: ['weight', 'diet', 'keto', 'loss', 'fat', 'health', 'fitness', 'pill', 'supplement', 'wellness', 'herb'], category: 'Health & Fitness' },
      { keywords: ['crypto', 'bitcoin', 'invest', 'trading', 'forex', 'finance', 'money', 'wealth', 'stock', 'equity'], category: 'Finance' },
      { keywords: ['ai', 'tool', 'software', 'saas', 'app', 'bot', 'copilot', 'marketing', 'write', 'video'], category: 'Software & SaaS' },
      { keywords: ['survival', 'gear', 'prepper', 'outdoor', 'camp', 'emergency', 'defense', 'tactical'], category: 'Survival' },
      { keywords: ['dog', 'cat', 'pet', 'train', 'puppy', 'bark', 'obedience'], category: 'Pet Care & Training' }
    ];

    let matchedCategory = 'E-Business & E-Marketing';
    for (const n of niches) {
      if (n.keywords.some(k => cleaned.toLowerCase().includes(k))) {
        matchedCategory = n.category;
        break;
      }
    }

    const networks = ['ClickBank', 'MaxBounty', 'JVZoo', 'Digistore24', 'CPALead'];
    const templates = [
      { suffix: 'Masterclass Course', type: 'CPS', commission: 50 },
      { suffix: 'Secrets Blueprint', type: 'CPS', commission: 75 },
      { suffix: 'Automated System', type: 'Revshare', commission: 40 },
      { suffix: 'Opt-in Lead Offer', type: 'CPL', commission: 100 },
      { suffix: 'Elite Starter Package', type: 'CPS', commission: 60 },
      { suffix: 'CPA Trial Offer', type: 'CPA', commission: 100 },
      { suffix: 'Traffic Acceleration Kit', type: 'CPS', commission: 85 },
      { suffix: 'Software Pro App', type: 'CPS', commission: 50 }
    ];

    return templates.map((tpl, i) => {
      const net = networks[i % networks.length];
      const payout = Math.round((25 + (i * 42) + (primaryWord.length * 4.5)) * 100) / 100;
      const gravity = Math.round((12 + (i * 28) + (payout / 12)) * 10) / 10;
      const metaAdsActive = Math.max(0, Math.round((i * 5) + (gravity / 15) - 2));
      const epc = Math.round((0.45 + (i * 0.38) + (payout / 180)) * 100) / 100;

      const offerName = `${primaryWord} ${secondaryWord} ${tpl.suffix}`.trim().replace(/\s+/g, ' ');
      const description = `${offerName} is a high-converting offer in the ${matchedCategory} space, providing affiliates with a payout of $${payout.toFixed(2)} per conversion with a competitive edge.`;

      const geos = ['US, CA, UK, AU', 'US Only', 'Worldwide', 'Tier 1 Only', 'US, CA, UK'];
      const geo = geos[i % geos.length];

      // Dynamic traffic source recommendations
      let trafficSources = [];
      if (matchedCategory === 'Finance') {
        trafficSources = [
          { name: 'NATIVE ADS (Taboola / MGID)', status: 'RECOMMENDED', cpc: '$0.35–$0.55', why: 'Editorial advertorials match financial offers well' },
          { name: 'EMAIL / SOLO ADS', status: 'WORTH TESTING', cpc: '$0.40–$0.70', why: 'MMO lists convert well on financial offers' },
          { name: 'GOOGLE SEARCH', status: 'PRICIER', cpc: '$1.20–$2.50', why: 'High intent but above break-even CPC' },
          { name: 'FACEBOOK / META', status: 'RESTRICTED', cpc: '-', why: 'Finance vertical — ad account risk' }
        ];
      } else if (matchedCategory === 'Health & Fitness') {
        trafficSources = [
          { name: 'NATIVE ADS (Taboola / MGID)', status: 'RECOMMENDED', cpc: '$0.25–$0.45', why: 'Health supplements and weight loss angles thrive on native' },
          { name: 'FACEBOOK / META', status: 'WORTH TESTING', cpc: '$0.40–$0.80', why: 'Mass appeal niche, but policy compliance is strict' },
          { name: 'EMAIL / SOLO ADS', status: 'WORTH TESTING', cpc: '$0.30–$0.50', why: 'Weight loss lists convert well with direct email angles' }
        ];
      } else {
        trafficSources = [
          { name: 'GOOGLE SEARCH', status: 'RECOMMENDED', cpc: '$0.60–$1.20', why: 'High intent searches match software and specific guides' },
          { name: 'FACEBOOK / META', status: 'RECOMMENDED', cpc: '$0.35–$0.70', why: 'Good visual demo and targeting potential' },
          { name: 'NATIVE ADS (Taboola / MGID)', status: 'WORTH TESTING', cpc: '$0.20–$0.40', why: 'Requires broad informational angles' }
        ];
      }

      // Generate active ads
      const ads = [];
      if (metaAdsActive > 0) {
        const pageNames = [`${primaryWord} Guides`, `${primaryWord} Tips`, 'Direct Marketer', 'SaaS Solutions', 'Healthy Lifestyle Hub'];
        const ctas = ['Learn More', 'Sign Up', 'Order Now', 'Download'];
        const angles = [
          `New simple blueprint to automate your ${primaryWord.toLowerCase()}`,
          `How to achieve breakthrough success with ${primaryWord.toLowerCase()}`,
          `Check out this strange trick for ${primaryWord.toLowerCase()}`,
          `Why professionals are switching to this ${primaryWord.toLowerCase()} method`
        ];
        
        for (let adIdx = 0; adIdx < Math.min(3, metaAdsActive); adIdx++) {
          ads.push({
            pageName: pageNames[(i + adIdx) % pageNames.length],
            duration: `${4 + adIdx * 8} days`,
            cta: ctas[(i + adIdx) % ctas.length],
            angle: angles[(i + adIdx) % angles.length]
          });
        }
      }

      return {
        id: `${net.toLowerCase()}_${i}_${primaryWord.toLowerCase()}`,
        name: offerName,
        network: net,
        payout,
        payoutType: tpl.type,
        commission: tpl.commission,
        geo,
        gravity,
        epc,
        metaAdsActive,
        category: matchedCategory,
        description,
        trafficSources,
        ads
      };
    });
  };

  // Grade calculation algorithm (Ninja Score)
  const calculateGradeMetrics = (offer) => {
    // 1. Payout strength (out of 10)
    let payoutScore = 3;
    if (offer.payout >= 200) payoutScore = 10;
    else if (offer.payout >= 100) payoutScore = 9;
    else if (offer.payout >= 50) payoutScore = 7;
    else if (offer.payout >= 20) payoutScore = 5;

    // 2. Market demand / gravity (out of 10)
    let demandScore = 3;
    if (offer.gravity >= 150) demandScore = 10;
    else if (offer.gravity >= 80) demandScore = 9;
    else if (offer.gravity >= 40) demandScore = 7;
    else if (offer.gravity >= 15) demandScore = 5;

    // 3. Competition level (out of 10) - lower ads running = better entry point score
    let competitionScore = 9;
    if (offer.metaAdsActive > 20) competitionScore = 4;
    else if (offer.metaAdsActive > 5) competitionScore = 6;

    // 4. Network reputation (out of 10)
    let reputationScore = 6;
    if (offer.network === 'ClickBank') reputationScore = 9;
    else if (offer.network === 'MaxBounty') reputationScore = 8;
    else if (offer.network === 'JVZoo') reputationScore = 7;

    // 5. EPC data availability (out of 10)
    let epcScore = 4;
    if (offer.epc >= 2.00) epcScore = 10;
    else if (offer.epc >= 1.20) epcScore = 8;
    else if (offer.epc >= 0.70) epcScore = 6;

    // 6. Traffic restrictions (out of 10)
    let trafficScore = 9;
    if (offer.category === 'Finance') trafficScore = 3; // Facebook policy restricted
    else if (offer.category === 'Health & Fitness' && offer.payoutType === 'CPS') trafficScore = 5; // Supplements policy restricted

    const overallScore = Math.round((payoutScore + demandScore + competitionScore + reputationScore + epcScore + trafficScore) / 6 * 10);
    
    let grade = 'C';
    let badgeText = '⚠️ C';
    let badgeColor = 'bg-yellow-500/10 text-[#eab308] border border-yellow-500/20';
    
    if (overallScore >= 93) {
      grade = 'A+';
      badgeText = '🏆 A+';
      badgeColor = 'bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/20';
    } else if (overallScore >= 85) {
      grade = 'A';
      badgeText = '🏆 A';
      badgeColor = 'bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/20';
    } else if (overallScore >= 78) {
      grade = 'B+';
      badgeText = '⭐ B+';
      badgeColor = 'bg-[#02B6FF]/10 text-[#02B6FF] border border-[#02B6FF]/20';
    } else if (overallScore >= 70) {
      grade = 'B';
      badgeText = '⭐ B';
      badgeColor = 'bg-[#02B6FF]/10 text-[#02B6FF] border border-[#02B6FF]/20';
    } else if (overallScore < 50) {
      grade = 'D';
      badgeText = '❌ D — avoid';
      badgeColor = 'bg-[#ef4444]/10 text-[#ef4444] border border-[#ef4444]/20';
    }

    return {
      score: overallScore,
      grade,
      badgeText,
      badgeColor,
      rubric: {
        payoutScore,
        demandScore,
        competitionScore,
        reputationScore,
        epcScore,
        trafficScore
      }
    };
  };

  // Run Scout Search Sequence
  const handleSearch = () => {
    if (!query.trim()) return;
    setIsSearching(true);
    setLoadingStep(0);
    setActiveDropdown(null);
  };

  // Loading Step Sequence Effect
  useEffect(() => {
    if (isSearching) {
      const timers = [
        setTimeout(() => setLoadingStep(1), 1500),
        setTimeout(() => setLoadingStep(2), 3000),
        setTimeout(() => setLoadingStep(3), 4500),
        setTimeout(() => {
          setLoadingStep(4);
          // Wait 1.5s more to display final results
          setTimeout(() => {
            const rawOffers = generateOffers(query);
            setOffers(rawOffers);
            
            // Sync search-view filters to refine-view filters
            setRefinePayout(payoutFilter);
            setRefineType(typeFilter);
            setRefineGeo(geoFilter);
            
            setIsSearching(false);
            setHasSearched(true);
          }, 1500);
        }, 6000)
      ];
      return () => timers.forEach(clearTimeout);
    }
  }, [isSearching]);

  // Handle Tag click
  const handleTagClick = (tag) => {
    setQuery(tag);
    // Auto-submit search
    setIsSearching(true);
    setLoadingStep(0);
  };

  // Open Details Modal
  const openDetails = (offer) => {
    setSelectedOffer(offer);
    
    // Set custom CPC default to Recommended CPC
    const affiliatePayout = offer.payout * (offer.commission / 100);
    const breakEven = affiliatePayout * 0.02 * 0.7;
    const recCPCVal = breakEven * 0.66;
    setCustomCPC(recCPCVal.toFixed(2));
  };

  // Filter & Sort Offers list
  const getFilteredOffers = () => {
    return offers
      .filter(offer => {
        // Payout filter
        if (refinePayout !== 'Any') {
          if (refinePayout === '$10–$50' && (offer.payout < 10 || offer.payout > 50)) return false;
          if (refinePayout === '$50–$150' && (offer.payout <= 50 || offer.payout > 150)) return false;
          if (refinePayout === '$150–$500' && (offer.payout <= 150 || offer.payout > 500)) return false;
          if (refinePayout === '$500+' && offer.payout <= 500) return false;
        }
        // Type filter
        if (refineType !== 'Any') {
          if (refineType === 'CPS (Sale)' && offer.payoutType !== 'CPS') return false;
          if (refineType === 'CPL (Lead)' && offer.payoutType !== 'CPL') return false;
          if (refineType === 'CPA' && offer.payoutType !== 'CPA') return false;
          if (refineType === 'Revshare' && offer.payoutType !== 'Revshare') return false;
        }
        // GEO filter
        if (refineGeo !== 'Any') {
          const lowerGeo = offer.geo.toLowerCase();
          if (refineGeo === 'US Only' && !(lowerGeo === 'us only' || lowerGeo === 'us')) return false;
          if (refineGeo === 'US/CA/UK/AU' && !(/us|ca|uk|au/i.test(lowerGeo))) return false;
          if (refineGeo === 'Tier 1' && !(/us|ca|uk|au|nz|de|fr|ie/i.test(lowerGeo))) return false;
          if (refineGeo === 'Worldwide' && !lowerGeo.includes('worldwide') && !lowerGeo.includes('ww') && lowerGeo !== 'worldwide') return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aMetrics = calculateGradeMetrics(a);
        const bMetrics = calculateGradeMetrics(b);
        if (sortField === 'score') {
          return bMetrics.score - aMetrics.score;
        } else if (sortField === 'payout') {
          return b.payout - a.payout;
        } else if (sortField === 'ads') {
          return b.metaAdsActive - a.metaAdsActive;
        } else if (sortField === 'epc') {
          return b.epc - a.epc;
        }
        return 0;
      });
  };

  const filteredOffersList = getFilteredOffers();

  // Hand-off: Build Funnel
  const handleBuildFunnel = (offer) => {
    // Map Payout Model
    let mappedPayoutModel = 'CPS/Sale';
    if (offer.payoutType === 'CPA') mappedPayoutModel = 'CPA/Lead';
    else if (offer.payoutType === 'CPL') mappedPayoutModel = 'CPL/Optin';
    else if (offer.payoutType === 'Revshare') mappedPayoutModel = 'RevShare';

    // Map Network to wizard dropdown values
    const validNetworks = ['JVZoo', 'ClickBank', 'WarriorPlus', 'Digistore24', 'CPALead', 'MaxBounty'];
    const networkVal = validNetworks.includes(offer.network) ? offer.network : 'Custom';

    const wizardData = {
      name: `${offer?.name || ''} — Campaign 1`,
      network: networkVal,
      customNetwork: !validNetworks.includes(offer.network) ? offer.network : '',
      affiliateLink: '', // Leave empty as requested
      niche: `${offer?.name || ''} - ${offer?.category || ''}`,
      payout: `$${offer.payout.toFixed(2)}`,
      payoutModel: mappedPayoutModel,
      networkEpc: `$${offer.epc.toFixed(2)}`,
      targetGeo: offer.geo
    };

    // Close details modal if open
    setSelectedOffer(null);
    
    // Call main openWizard function
    openWizard(wizardData);
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleOutsideClick = () => {
      setActiveDropdown(null);
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#0d0f14] font-mono text-slate-300 overflow-hidden">
      
      {/* SCREEN 1: SEARCH STATE */}
      {!isSearching && !hasSearched && (
        <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 max-h-[85vh]">
          <div className="max-w-2xl w-full text-center space-y-8 animate-in fade-in duration-300">
            {/* Header */}
            <div className="space-y-3">
              <div className="text-4xl">🎯</div>
              <h2 className="text-3xl font-black text-white tracking-tight">Offer Scout</h2>
              <p className="text-xs text-[#6b7080] font-mono max-w-md mx-auto">
                Find your next winning offer. AI-researched, coach-approved.
              </p>
            </div>

            {/* Search Bar */}
            <div className="space-y-4">
              <div className="flex bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-1.5 focus-within:border-orange-500/60 shadow-lg shadow-black/40 transition-all">
                <input 
                  type="text" 
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. weight loss, crypto investing, survival gear..."
                  className="flex-1 bg-transparent border-0 outline-none text-white px-4 py-3 text-xs placeholder-[#6b7080]"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button 
                  onClick={handleSearch}
                  disabled={!query.trim()}
                  className="bg-orange-500 hover:bg-orange-400 text-white font-bold text-[10px] uppercase tracking-widest px-6 py-3 rounded-[6px] transition-all flex items-center shrink-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Search size={14} className="mr-2" /> Scout Offers
                </button>
              </div>

              {/* Filters row */}
              <div className="flex flex-wrap justify-center gap-3 pt-1">
                {/* Payout Filter */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button 
                    onClick={() => setActiveDropdown(activeDropdown === 'payout' ? null : 'payout')}
                    className={`px-3 py-2 bg-[#1a1d27] hover:border-[rgba(255,255,255,0.15)] border border-[rgba(255,255,255,0.08)] rounded-[6px] text-[10px] font-bold flex items-center transition-all ${payoutFilter !== 'Any' ? 'text-orange-500 border-orange-500/20 bg-orange-500/5' : ''}`}
                  >
                    💰 Payout: {payoutFilter} <ChevronDown size={10} className="ml-1.5 opacity-60" />
                  </button>
                  {activeDropdown === 'payout' && (
                    <div className="absolute top-full mt-1 left-0 bg-[#161922] border border-[rgba(255,255,255,0.12)] rounded-[6px] overflow-hidden z-[110] shadow-xl w-40 animate-in fade-in slide-in-from-top-1 duration-150">
                      {['Any', '$10–$50', '$50–$150', '$150–$500', '$500+'].map((opt) => (
                        <button 
                          key={opt}
                          onClick={() => { setPayoutFilter(opt); setActiveDropdown(null); }}
                          className={`w-full text-left px-3 py-2.5 hover:bg-slate-800 text-[9px] font-mono transition-colors border-b border-slate-800/60 last:border-b-0 ${payoutFilter === opt ? 'text-orange-500 bg-orange-500/5' : 'text-slate-300'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Type Filter */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button 
                    onClick={() => setActiveDropdown(activeDropdown === 'type' ? null : 'type')}
                    className={`px-3 py-2 bg-[#1a1d27] hover:border-[rgba(255,255,255,0.15)] border border-[rgba(255,255,255,0.08)] rounded-[6px] text-[10px] font-bold flex items-center transition-all ${typeFilter !== 'Any' ? 'text-orange-500 border-orange-500/20 bg-orange-500/5' : ''}`}
                  >
                    📦 Type: {typeFilter} <ChevronDown size={10} className="ml-1.5 opacity-60" />
                  </button>
                  {activeDropdown === 'type' && (
                    <div className="absolute top-full mt-1 left-0 bg-[#161922] border border-[rgba(255,255,255,0.12)] rounded-[6px] overflow-hidden z-[110] shadow-xl w-40 animate-in fade-in slide-in-from-top-1 duration-150">
                      {['Any', 'CPS (Sale)', 'CPL (Lead)', 'CPA', 'Revshare'].map((opt) => (
                        <button 
                          key={opt}
                          onClick={() => { setTypeFilter(opt); setActiveDropdown(null); }}
                          className={`w-full text-left px-3 py-2.5 hover:bg-slate-800 text-[9px] font-mono transition-colors border-b border-slate-800/60 last:border-b-0 ${typeFilter === opt ? 'text-orange-500 bg-orange-500/5' : 'text-slate-300'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* GEO Filter */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button 
                    onClick={() => setActiveDropdown(activeDropdown === 'geo' ? null : 'geo')}
                    className={`px-3 py-2 bg-[#1a1d27] hover:border-[rgba(255,255,255,0.15)] border border-[rgba(255,255,255,0.08)] rounded-[6px] text-[10px] font-bold flex items-center transition-all ${geoFilter !== 'Any' ? 'text-orange-500 border-orange-500/20 bg-orange-500/5' : ''}`}
                  >
                    🌎 GEO: {geoFilter} <ChevronDown size={10} className="ml-1.5 opacity-60" />
                  </button>
                  {activeDropdown === 'geo' && (
                    <div className="absolute top-full mt-1 left-0 bg-[#161922] border border-[rgba(255,255,255,0.12)] rounded-[6px] overflow-hidden z-[110] shadow-xl w-44 animate-in fade-in slide-in-from-top-1 duration-150">
                      {['Any', 'Worldwide', 'US Only', 'US/CA/UK/AU', 'Tier 1'].map((opt) => (
                        <button 
                          key={opt}
                          onClick={() => { setGeoFilter(opt); setActiveDropdown(null); }}
                          className={`w-full text-left px-3 py-2.5 hover:bg-slate-800 text-[9px] font-mono transition-colors border-b border-slate-800/60 last:border-b-0 ${geoFilter === opt ? 'text-orange-500 bg-orange-500/5' : 'text-slate-300'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick search tags */}
            <div className="flex justify-center items-center gap-2 text-[10px] text-slate-500 pt-4 flex-wrap">
              <span>🔥 Trending:</span>
              {['Weight Loss', 'Crypto', 'AI Tools', 'Survival', 'Forex'].map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="px-2.5 py-1 bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-400 hover:text-white rounded-[6px] transition-colors cursor-pointer font-bold"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SCREEN 2: LOADING STATE */}
      {isSearching && (
        <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 bg-[#0d0f14]">
          <div className="max-w-md w-full space-y-6">
            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] text-center mb-8">AI COACH IN ACTION</h3>
            
            <div className="space-y-4 bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-indigo-500 animate-pulse"></div>

              {/* Step 1: OfferVault */}
              <div className={`flex items-center space-x-3 transition-opacity duration-300 ${loadingStep >= 0 ? 'opacity-100' : 'opacity-20'}`}>
                {loadingStep > 0 ? (
                  <CheckCircle2 size={16} className="text-[#22c55e] shrink-0" />
                ) : loadingStep === 0 ? (
                  <div className="size-2 bg-orange-500 rounded-full animate-ping shrink-0" />
                ) : (
                  <div className="size-2 bg-slate-700 rounded-full shrink-0" />
                )}
                <span className={`text-xs ${loadingStep === 0 ? 'text-white font-bold' : 'text-slate-400'}`}>
                  🔍 Searching OfferVault across 200+ networks...
                </span>
              </div>

              {/* Step 2: ClickBank */}
              <div className={`flex items-center space-x-3 transition-opacity duration-300 ${loadingStep >= 1 ? 'opacity-100' : 'opacity-20'}`}>
                {loadingStep > 1 ? (
                  <CheckCircle2 size={16} className="text-[#22c55e] shrink-0" />
                ) : loadingStep === 1 ? (
                  <div className="size-2 bg-orange-500 rounded-full animate-ping shrink-0" />
                ) : (
                  <div className="size-2 bg-slate-700 rounded-full shrink-0" />
                )}
                <span className={`text-xs ${loadingStep === 1 ? 'text-white font-bold' : 'text-slate-400'}`}>
                  📊 Checking ClickBank gravity scores...
                </span>
              </div>

              {/* Step 3: Meta Ad Library */}
              <div className={`flex items-center space-x-3 transition-opacity duration-300 ${loadingStep >= 2 ? 'opacity-100' : 'opacity-20'}`}>
                {loadingStep > 2 ? (
                  <CheckCircle2 size={16} className="text-[#22c55e] shrink-0" />
                ) : loadingStep === 2 ? (
                  <div className="size-2 bg-orange-500 rounded-full animate-ping shrink-0" />
                ) : (
                  <div className="size-2 bg-slate-700 rounded-full shrink-0" />
                )}
                <span className={`text-xs ${loadingStep === 2 ? 'text-white font-bold' : 'text-slate-400'}`}>
                  📱 Scanning Meta Ad Library for active campaigns...
                </span>
              </div>

              {/* Step 4: Grading */}
              <div className={`flex items-center space-x-3 transition-opacity duration-300 ${loadingStep >= 3 ? 'opacity-100' : 'opacity-20'}`}>
                {loadingStep > 3 ? (
                  <CheckCircle2 size={16} className="text-[#22c55e] shrink-0" />
                ) : loadingStep === 3 ? (
                  <div className="size-2 bg-orange-500 rounded-full animate-ping shrink-0" />
                ) : (
                  <div className="size-2 bg-slate-700 rounded-full shrink-0" />
                )}
                <span className={`text-xs ${loadingStep === 3 ? 'text-white font-bold' : 'text-slate-400'}`}>
                  🧠 Ninja is grading each offer...
                </span>
              </div>
            </div>

            <div className="text-center font-bold text-[9px] text-[#6b7080] tracking-wider animate-pulse pt-2">
              ESTIMATING EPC & BREAK-EVEN METRICS...
            </div>
          </div>
        </div>
      )}

      {/* SCREEN 3: RESULTS PAGE */}
      {!isSearching && hasSearched && (
        <div className="flex-1 flex overflow-hidden">
          
          {/* Left Sidebar (380px) */}
          <aside className="w-[380px] bg-[#13161d] border-r border-[rgba(255,255,255,0.08)] flex flex-col shrink-0">
            {/* Sidebar Header */}
            <div className="p-6 border-b border-[rgba(255,255,255,0.08)] shrink-0 flex items-center justify-between">
              <div>
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">OFFER RECON</span>
                <span className="text-base font-black text-orange-500 mt-1 block">
                  {filteredOffersList.length} {filteredOffersList.length === 1 ? 'offer' : 'offers'} found
                </span>
              </div>
              <button 
                onClick={() => setHasSearched(false)}
                className="p-2 text-[#6b7080] hover:text-white bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[6px] transition-colors"
                title="New Search"
              >
                <ArrowLeft size={14} />
              </button>
            </div>

            {/* Sidebar Refine Filters */}
            <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar">
              
              {/* Search input to edit query */}
              <div className="space-y-2">
                <label className="block text-[9px] text-slate-500 font-black uppercase">Search Term</label>
                <div className="flex bg-[#0d0f14] border border-[rgba(255,255,255,0.08)] rounded-[6px] overflow-hidden p-1 focus-within:border-orange-500/60">
                  <input 
                    type="text" 
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 bg-transparent border-0 outline-none text-white text-[11px] px-2"
                  />
                  <button 
                    onClick={handleSearch}
                    className="p-2 bg-orange-500 hover:bg-orange-400 text-white rounded-[4px] transition-colors"
                  >
                    <Search size={10} />
                  </button>
                </div>
              </div>

              {/* Sort filter */}
              <div className="space-y-2">
                <label className="block text-[9px] text-slate-500 font-black uppercase">Sort Offers By</label>
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  className="w-full bg-[#0d0f14] border border-[rgba(255,255,255,0.08)] text-white px-3 py-2.5 rounded-[6px] outline-none text-xs font-bold appearance-none cursor-pointer"
                >
                  <option value="score">Ninja Score (Highest First)</option>
                  <option value="payout">Highest Payout ($)</option>
                  <option value="ads">Most Active Ads (Meta Library)</option>
                  <option value="epc">Best Estimated EPC</option>
                </select>
              </div>

              <div className="border-t border-[rgba(255,255,255,0.05)] pt-6 space-y-5">
                
                {/* Refine Payout */}
                <div className="space-y-2">
                  <label className="block text-[9px] text-slate-500 font-black uppercase">Refine Payout</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Any', '$10–$50', '$50–$150', '$150–$500', '$500+'].map((opt) => (
                      <button 
                        key={opt}
                        onClick={() => setRefinePayout(opt)}
                        className={`text-center py-2 border rounded-[4px] text-[9px] font-bold transition-all ${refinePayout === opt ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-[#0d0f14] border-[rgba(255,255,255,0.05)] text-slate-400 hover:text-white hover:border-[rgba(255,255,255,0.12)]'}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Refine Type */}
                <div className="space-y-2">
                  <label className="block text-[9px] text-slate-500 font-black uppercase">Payout Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Any', 'CPS (Sale)', 'CPL (Lead)', 'CPA', 'Revshare'].map((opt) => (
                      <button 
                        key={opt}
                        onClick={() => setRefineType(opt)}
                        className={`text-center py-2 border rounded-[4px] text-[9px] font-bold transition-all ${refineType === opt ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-[#0d0f14] border-[rgba(255,255,255,0.05)] text-slate-400 hover:text-white hover:border-[rgba(255,255,255,0.12)]'}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Refine GEO */}
                <div className="space-y-2">
                  <label className="block text-[9px] text-slate-500 font-black uppercase">Target Geography</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Any', 'Worldwide', 'US Only', 'US/CA/UK/AU', 'Tier 1'].map((opt) => (
                      <button 
                        key={opt}
                        onClick={() => setRefineGeo(opt)}
                        className={`text-center py-2 border rounded-[4px] text-[9px] font-bold transition-all ${refineGeo === opt ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' : 'bg-[#0d0f14] border-[rgba(255,255,255,0.05)] text-slate-400 hover:text-white hover:border-[rgba(255,255,255,0.12)]'}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

              </div>

            </div>
          </aside>

          {/* Right Main Grid */}
          <main className="flex-1 flex flex-col min-h-0 bg-[#0d0f14]">
            
            {/* Results Title Area */}
            <div className="px-8 py-5 border-b border-[rgba(255,255,255,0.08)] bg-[#13161d]/30 flex justify-between items-center shrink-0">
              <div className="flex items-center space-x-2">
                <span className="text-[10px] text-slate-500 font-bold uppercase">Results for:</span>
                <span className="text-xs font-bold text-white px-2 py-0.5 bg-slate-900 border border-slate-800 rounded">{query}</span>
              </div>
              
              <div className="text-[9px] text-[#6b7080] font-bold flex items-center">
                <Info size={12} className="mr-1.5" /> Showing top 12 vetted offers. Click Full Analysis to view traffic and ad plans.
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
              
              {/* EMPTY STATE */}
              {filteredOffersList.length === 0 ? (
                <div className="max-w-xl mx-auto text-center py-16 space-y-6 bg-[#1a1d27]/40 border border-[rgba(255,255,255,0.05)] rounded-[10px] p-10 animate-in fade-in duration-300">
                  <div className="text-3xl">🎯</div>
                  <h4 className="text-base font-black text-white uppercase tracking-wider">No offers found for "{query}"</h4>
                  
                  <div className="text-left space-y-4 pt-4 border-t border-slate-800/60">
                    <p className="text-xs text-orange-500 font-bold">The AI Coach suggests:</p>
                    <ul className="text-xs text-slate-400 space-y-2 pl-4 list-disc leading-relaxed">
                      <li>Try broader terms (e.g. use <strong className="text-slate-300 font-mono">"weight loss"</strong> vs <strong className="text-slate-300 font-mono">"keto pills"</strong>)</li>
                      <li>Check ClickBank directly for very specific niche offers</li>
                      <li>Ask Ninja Coach directly to find offers in this space:</li>
                    </ul>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={() => handleTagClick(`Find me offers in the ${query} space`)}
                      className="w-full text-left bg-slate-950 border border-slate-800 hover:border-slate-700 text-slate-300 hover:text-white px-4 py-3 rounded-[6px] text-xs font-mono transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <span>💡 "Find me offers in the <strong className="text-orange-400 font-mono">{query}</strong> space"</span>
                      <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ) : (
                
/* CARDS GRID */
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {filteredOffersList.slice(0, 12).map((offer) => {
                    const { score, grade, badgeText, badgeColor } = calculateGradeMetrics(offer);
                    const breakEvenCPC = (offer.payout * (offer.commission / 100)) * 0.02 * 0.7;
                    const compLevel = offer.metaAdsActive <= 5 ? 'Low' : offer.metaAdsActive <= 20 ? 'Medium' : 'High';
                    const compColor = compLevel === 'Low' ? 'text-[#22c55e]' : compLevel === 'Medium' ? 'text-[#eab308]' : 'text-[#ef4444]';

                    // Calculate dynamic Coach tip (V1.1 Decision Tree)
                    let coachTipText = "";
                    let coachTipColorClass = ""; // tailwind colors: text-[#eab308], text-[#f97316], text-[#6b7080], text-[#22c55e]
                    
                    if (breakEvenCPC > 2.00) {
                      coachTipText = "⚠️ High break-even CPC — requires experienced traffic buying";
                      coachTipColorClass = "text-[#eab308]";
                    } else if (compLevel === "High" && offer.metaAdsActive > 30) {
                      coachTipText = "⚠️ High ad saturation — find an untested angle before entering";
                      coachTipColorClass = "text-[#eab308]";
                    } else if (offer.payoutType === "CPL") {
                      coachTipText = "💡 Lead offer — lower barrier to convert, good for beginners";
                      coachTipColorClass = "text-[#6b7080]";
                    } else if (compLevel === "Low" && offer.metaAdsActive < 5) {
                      coachTipText = "🎯 Low competition on Meta — good entry point right now";
                      coachTipColorClass = "text-[#f97316]";
                    } else if (offer.commission === 100) {
                      coachTipText = "💡 100% commission — likely a funnel front-end, check for upsells";
                      coachTipColorClass = "text-[#6b7080]";
                    } else if (offer.payoutType === "Revshare" || offer.payoutType === "RevShare") {
                      coachTipText = "⚠️ Revshare — slower to profit, not recommended for beginners";
                      coachTipColorClass = "text-[#eab308]";
                    } else if (offer.gravity > 150) {
                      coachTipText = "🔥 High gravity — proven converter but expect competition";
                      coachTipColorClass = "text-[#22c55e]";
                    } else if (offer.gravity < 30) {
                      coachTipText = "⚠️ Low gravity — unproven offer, validate carefully before scaling";
                      coachTipColorClass = "text-[#eab308]";
                    } else if (offer.epc > 3.00) {
                      coachTipText = "💰 Strong EPC — room for profitable campaigns if CPC stays low";
                      coachTipColorClass = "text-[#22c55e]";
                    } else {
                      coachTipText = "💡 Steady offer — run standard Phase 1 validation (500 clicks)";
                      coachTipColorClass = "text-[#6b7080]";
                    }

                    return (
                      <div 
                        key={offer.id} 
                        className="bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-6 hover:border-[rgba(255,255,255,0.15)] hover:shadow-xl hover:shadow-black/20 transition-all flex flex-col justify-between space-y-6"
                      >
                        {/* Card Header */}
                        <div className="flex justify-between items-start space-x-3">
                          <h4 className="text-[13px] font-black text-white line-clamp-1">{offer?.name || ''}</h4>
                          <span className={`px-2 py-1 rounded-[6px] text-[9px] font-black uppercase tracking-wider shrink-0 ${badgeColor}`}>
                            {badgeText}
                          </span>
                        </div>

                        {/* Card Body */}
                        <div className="grid grid-cols-2 gap-4 text-[10px]">
                          {/* Left Column */}
                          <div className="space-y-2 border-r border-slate-800/80 pr-2">
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">NETWORK:</span>
                              <span className="text-white font-bold">{offer.network}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">PAYOUT:</span>
                              <span className="text-white font-mono font-bold">${offer.payout.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">TYPE:</span>
                              <span className="text-white font-bold">{offer.payoutType}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">COMMISSION:</span>
                              <span className="text-white font-bold">{offer.commission}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">GEO:</span>
                              <span className="text-white font-bold max-w-[100px] truncate text-right" title={offer.geo}>{offer.geo}</span>
                            </div>
                          </div>

                          {/* Right Column */}
                          <div className="space-y-2 pl-2">
                            <div className="flex justify-between items-center">
                              <span className="text-[#6b7080]">GRAVITY:</span>
                              <span className="text-white font-mono font-bold">{offer.gravity}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">EST. EPC:</span>
                              <span className="text-emerald-400 font-mono font-bold">${offer.epc.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-[#6b7080]">BREAK-EVEN CPC:</span>
                              <div className="flex items-center space-x-1.5 font-mono">
                                <span className={`font-bold ${
                                  breakEvenCPC <= 0.80 ? 'text-[#22c55e]' : 
                                  breakEvenCPC <= 1.50 ? 'text-[#eab308]' : 
                                  'text-[#ef4444]'
                                }`}>
                                  ${breakEvenCPC.toFixed(2)}
                                </span>
                                {breakEvenCPC > 0.80 && (
                                  <span 
                                    className="text-[#eab308] text-[8px] font-bold cursor-help select-none inline-flex items-center"
                                    title="Beginner recommended max: $0.80. This offer requires advanced traffic skills."
                                  >
                                    {breakEvenCPC <= 1.50 ? '⚠️ MODERATE' : '⚠️ HIGH'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">COMPETITION:</span>
                              <span className={`font-bold ${compColor}`}>{compLevel}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#6b7080]">META ADS:</span>
                              <span className="text-white font-bold">{offer.metaAdsActive} active</span>
                            </div>
                          </div>
                        </div>

                        {/* Card Footer */}
                        <div className="border-t border-slate-800/80 pt-4 flex flex-col space-y-3">
                          <span className={`text-[11px] italic font-mono ${coachTipColorClass}`}>
                            {coachTipText}
                          </span>

                          <div className="text-[10px] text-[#6b7080] flex items-center">
                            <span>🔗 Get your link:&nbsp;&nbsp;</span>
                            {NETWORK_URLS[offer.network] ? (
                              <a 
                                href={NETWORK_URLS[offer.network]} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-[#6b7080] hover:text-white hover:underline transition-colors"
                              >
                                {offer.network} ↗
                              </a>
                            ) : (
                              <span>{offer.network}</span>
                            )}
                          </div>

                          <div className="flex items-center space-x-2 pt-1">
                            <button
                              onClick={() => {
                                if (NETWORK_URLS[offer.network]) {
                                  window.open(NETWORK_URLS[offer.network], '_blank');
                                }
                              }}
                              className="px-3 py-2 bg-transparent hover:bg-[rgba(255,255,255,0.05)] text-[#e8eaf0] border border-[rgba(255,255,255,0.15)] hover:border-[rgba(255,255,255,0.30)] rounded-[6px] text-[11px] font-mono tracking-[0.04em] transition-all cursor-pointer text-center"
                            >
                              🔗 View Offer
                            </button>
                            <button 
                              onClick={() => openDetails(offer)}
                              className="px-3 py-2 bg-slate-900 hover:bg-slate-850 text-slate-300 hover:text-white rounded-[6px] text-[9px] font-bold uppercase transition-colors cursor-pointer border border-slate-800"
                            >
                              📋 Full Analysis
                            </button>
                          </div>
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}

            </div>
          </main>
        </div>
      )}

      {/* DETAIL MODAL */}
      {selectedOffer && (() => {
        const { score, grade, rubric } = calculateGradeMetrics(selectedOffer);
        const affiliatePayout = selectedOffer.payout * (selectedOffer.commission / 100);
        const breakEvenCPC = affiliatePayout * 0.02 * 0.7; // affiliatePayout * 0.014
        
        // Calculator live updates
        const parsedCustomCPC = parseFloat(customCPC) || 0.01;
        const clicksNeeded = Math.round(affiliatePayout / parsedCustomCPC);
        const convRateNeeded = ((parsedCustomCPC / affiliatePayout) * 100);
        const budgetFor500 = Math.round(parsedCustomCPC * 500);

        return (
          <div className="fixed inset-0 z-[200] flex justify-end bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300 font-mono">
            <div className="bg-[#0d0f14] border-l border-[rgba(255,255,255,0.08)] w-full max-w-3xl flex flex-col h-screen shadow-2xl animate-in slide-in-from-right duration-350">
              
              {/* Modal Header */}
              <div className="px-8 py-6 border-b border-[rgba(255,255,255,0.08)] flex justify-between items-center bg-[#13161d] shrink-0">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-600/10 text-blue-400 rounded-lg border border-blue-500/20">
                    <Compass size={18} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-white uppercase tracking-wider">{selectedOffer?.name || ''}</h3>
                    <p className="text-[9px] text-[#6b7080] font-black uppercase mt-0.5">{selectedOffer?.network || ''} Network Offer</p>
                  </div>
                </div>
                
                <button 
                  onClick={() => setSelectedOffer(null)}
                  className="p-2 text-[#6b7080] hover:text-white hover:bg-slate-900 rounded-[6px] border border-slate-800 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                
                {/* Section 1: Overview */}
                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-orange-500 uppercase tracking-wider border-b border-slate-800/80 pb-2">Section 1 — Offer Overview</h4>
                  <div className="bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-5 space-y-4">
                    <div className="text-xs text-slate-300 leading-relaxed font-sans">
                      {selectedOffer.description}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[10px] pt-4 border-t border-slate-800/60">
                      <div>
                        <span className="text-[#6b7080] block uppercase mb-1">Payout Size:</span>
                        <span className="text-white font-bold">${selectedOffer.payout.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-[#6b7080] block uppercase mb-1">Commission:</span>
                        <span className="text-white font-bold">{selectedOffer.commission}%</span>
                      </div>
                      <div>
                        <span className="text-[#6b7080] block uppercase mb-1">Target GEO:</span>
                        <span className="text-white font-bold">{selectedOffer.geo}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Rubric Grade Breakdown */}
                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-orange-500 uppercase tracking-wider border-b border-slate-800/80 pb-2">Section 2 — Ninja Grade Breakdown</h4>
                  <div className="bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-5 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-white font-bold">OFFER GRADE RUBRIC:</span>
                      <span className="text-xs font-black px-3 py-1 bg-slate-900 border border-slate-800 rounded-[6px] text-white">
                        GRADE: {grade}
                      </span>
                    </div>

                    <div className="text-[11px] space-y-3 font-mono">
                      <div className="flex justify-between items-center border-b border-slate-800/30 pb-2">
                        <span className="text-slate-400">✅ Payout strength</span>
                        <span className="text-white font-bold">{rubric.payoutScore}/10</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-800/30 pb-2">
                        <span className="text-slate-400">✅ Market demand (Gravity)</span>
                        <span className="text-white font-bold">{rubric.demandScore}/10</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-800/30 pb-2">
                        <span className="text-slate-400">
                          {rubric.competitionScore >= 8 ? '✅' : '⚠️'} Competition level
                        </span>
                        <span className="text-white font-bold">
                          {rubric.competitionScore}/10 ({selectedOffer.metaAdsActive <= 5 ? 'low' : selectedOffer.metaAdsActive <= 20 ? 'medium' : 'high'})
                        </span>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-800/30 pb-2">
                        <span className="text-slate-400">✅ Network reputation</span>
                        <span className="text-white font-bold">{rubric.reputationScore}/10</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-800/30 pb-2">
                        <span className="text-slate-400">✅ EPC data available</span>
                        <span className="text-white font-bold">{rubric.epcScore}/10</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-slate-800/30 pb-2">
                        <span className={rubric.trafficScore <= 5 ? '❌' : '✅'}>Traffic restrictions</span>
                        <span className="text-white font-bold">
                          {rubric.trafficScore}/10 ({selectedOffer.category === 'Finance' ? 'Facebook limited' : selectedOffer.category === 'Health & Fitness' ? 'Supplements limited' : 'low restrictions'})
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 font-bold text-white text-xs">
                        <span>OVERALL SCORE</span>
                        <span className="text-orange-500">{score}/100 → {grade}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Interactive Break-Even Calculator */}
                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-orange-500 uppercase tracking-wider border-b border-slate-800/80 pb-2">Section 3 — Break-Even Calculator</h4>
                  <div className="bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-5 space-y-5">
                    
                    <div className="grid grid-cols-2 gap-4 text-[10px] border-b border-slate-800/60 pb-3">
                      <div>
                        <span className="text-[#6b7080] block uppercase mb-1">Payout:</span>
                        <span className="text-white font-bold font-mono">${selectedOffer.payout.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-[#6b7080] block uppercase mb-1">Your Commission:</span>
                        <span className="text-white font-bold font-mono">
                          {selectedOffer.commission}% → ${affiliatePayout.toFixed(2)} per sale
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-[10px] border-b border-slate-800/60 pb-3">
                      <div>
                        <span className="text-[#6b7080] block uppercase mb-1">Break-even CPC:</span>
                        <span className="text-orange-400 font-bold font-mono">${breakEvenCPC.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-[#6b7080] block uppercase mb-1">Recommended max CPC:</span>
                        <span className="text-[#22c55e] font-bold font-mono">${(breakEvenCPC * 0.66).toFixed(2)} (33% profit margin)</span>
                      </div>
                    </div>

                    {/* Interactive Input */}
                    <div className="space-y-3 pt-2">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] text-white font-bold uppercase">Adjust Your Max CPC</label>
                        <div className="flex items-center bg-[#0d0f14] border border-[rgba(255,255,255,0.08)] rounded-[6px] px-2.5 py-1">
                          <span className="text-slate-500 text-[10px] mr-1">$</span>
                          <input 
                            type="number" 
                            step="0.01" 
                            min="0.01" 
                            max="10" 
                            value={customCPC}
                            onChange={(e) => setCustomCPC(e.target.value)}
                            className="bg-transparent border-0 outline-none text-white text-[11px] font-mono w-14 font-bold text-right"
                          />
                        </div>
                      </div>
                      
                      <div className="bg-[#0d0f14] border border-[rgba(255,255,255,0.05)] rounded-[6px] p-4 text-[10px] space-y-2 text-slate-400">
                        <div className="flex justify-between">
                          <span>Clicks needed per 1 sale:</span>
                          <strong className="text-white font-mono">{clicksNeeded} clicks</strong>
                        </div>
                        <div className="flex justify-between">
                          <span>Target conversion rate needed:</span>
                          <strong className="text-white font-mono">{convRateNeeded.toFixed(2)}%</strong>
                        </div>
                        <div className="flex justify-between">
                          <span>Phase 1 budget (for 500 clicks):</span>
                          <strong className="text-white font-mono">${budgetFor500}</strong>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Section 4: Traffic Recommendations */}
                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-orange-500 uppercase tracking-wider border-b border-slate-800/80 pb-2">Section 4 — Traffic Source Recommendations</h4>
                  <div className="space-y-3">
                    {selectedOffer.trafficSources.map((source, i) => (
                      <div key={i} className="bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[10px]">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-bold">#{i+1} {source?.name || ''}</span>
                            <span className={`px-1.5 py-0.5 rounded-[4px] text-[8px] font-black uppercase tracking-wider ${
                              source.status === 'RECOMMENDED' ? 'bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/20' : 
                              source.status === 'WORTH TESTING' ? 'bg-[#02B6FF]/10 text-[#02B6FF] border border-[#02B6FF]/20' :
                              source.status === 'PRICIER' ? 'bg-[#eab308]/10 text-[#eab308] border border-[#eab308]/20' :
                              'bg-[#ef4444]/10 text-[#ef4444] border border-[#ef4444]/20'
                            }`}>
                              {source.status}
                            </span>
                          </div>
                          <p className="text-slate-400 font-sans leading-relaxed">{source.why}</p>
                        </div>
                        <div className="shrink-0 text-right md:border-l md:border-slate-800/80 md:pl-4 min-w-[90px]">
                          <span className="text-[#6b7080] block text-[8px] uppercase">EST. CPC</span>
                          <span className="text-white font-bold font-mono">{source.cpc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 5: Meta Ad Intelligence */}
                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-orange-500 uppercase tracking-wider border-b border-slate-800/80 pb-2">Section 5 — Meta Ad Intelligence</h4>
                  
                  {selectedOffer.ads && selectedOffer.ads.length > 0 ? (
                    <div className="space-y-4">
                      {selectedOffer.ads.map((ad, idx) => (
                        <div key={idx} className="bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-5 space-y-3 text-[10px]">
                          <div className="flex justify-between items-center border-b border-slate-800/50 pb-2">
                            <span className="text-white font-bold flex items-center">
                              <span className="size-2 bg-[#22c55e] rounded-full mr-2 animate-pulse"></span>
                              {ad.pageName}
                            </span>
                            <span className="text-slate-400 font-mono">Running: {ad.duration}</span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <span className="text-[#6b7080] block uppercase mb-1">Dominant Angle (AI Extracted):</span>
                              <p className="text-slate-200 font-sans leading-relaxed italic">"{ad.angle}"</p>
                            </div>
                            <div>
                              <span className="text-[#6b7080] block uppercase mb-1">CTA Type:</span>
                              <span className="px-2.5 py-1 bg-slate-900 border border-slate-800 rounded font-bold text-white">
                                {ad.cta}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-[#1a1d27] border border-[rgba(255,255,255,0.08)] rounded-[10px] p-6 text-center text-xs space-y-2">
                      <div className="text-slate-400">No active Facebook ads detected for this offer.</div>
                      <p className="text-[#6b7080] max-w-md mx-auto text-[10px] font-sans leading-relaxed">
                        This could mean low competition — or that Facebook doesn't convert for this vertical. Check native ads first.
                      </p>
                    </div>
                  )}
                </div>

                {/* Section 6: Coach Recommendation */}
                <div className="space-y-4">
                  <h4 className="text-[11px] font-black text-orange-500 uppercase tracking-wider border-b border-slate-800/80 pb-2">Section 6 — Coach Recommendation</h4>
                  <div className="bg-orange-500/5 border border-orange-500/10 rounded-[10px] p-6 text-xs leading-relaxed text-[#f97316] italic relative overflow-hidden font-sans">
                    <div className="absolute top-0 left-0 w-1 h-full bg-orange-500"></div>
                    <p>
                      "This offer has a solid payout of ${selectedOffer.payout.toFixed(2)} and {selectedOffer.gravity > 80 ? 'strong' : 'decent'} gravity, but you're entering a {selectedOffer.metaAdsActive > 15 ? 'high' : 'medium'}-competition space. The good news: most affiliates are running standard angles. Start with {selectedOffer.category === 'Finance' ? 'native ads' : 'native or search ads'} at ${(breakEvenCPC * 0.66).toFixed(2)} max CPC. Budget ${budgetFor500} for Phase 1. If your EPC doesn't hit ${(breakEvenCPC * 1.5).toFixed(2)} after 500 clicks, swap the ad creative angle before you swap the offer."
                    </p>
                  </div>
                </div>

              </div>

              {/* Modal Footer */}
              <div className="px-8 py-6 border-t border-[rgba(255,255,255,0.08)] bg-[#13161d] flex flex-col space-y-3 shrink-0">
                <button
                  onClick={() => {
                    const marketplaceUrls = {
                      ClickBank: 'https://accounts.clickbank.com/marketplace.htm',
                      JVZoo: 'https://www.jvzoo.com/affiliate/browse',
                      MaxBounty: 'https://www.maxbounty.com/offers.cfm',
                      Digistore24: 'https://www.digistore24.com/marketplace',
                      CPALead: 'https://www.cpalead.com/dashboard/campaigns'
                    };
                    const url = marketplaceUrls[selectedOffer.network];
                    if (url) {
                      window.open(url, '_blank');
                    }
                  }}
                  className="w-full py-2.5 bg-transparent hover:bg-[rgba(255,255,255,0.05)] text-[#e8eaf0] border border-[rgba(255,255,255,0.15)] hover:border-[rgba(255,255,255,0.30)] rounded-[6px] text-[11px] font-mono tracking-[0.04em] transition-all cursor-pointer text-center"
                >
                  → Get My Affiliate Link from {selectedOffer.network} ↗
                </button>

                <div className="text-[10px] text-[#6b7080] italic text-center font-mono">
                  Don't have your link yet? Grab it first — you'll paste it in Step 1 of the wizard.
                </div>

                <button
                  onClick={() => handleBuildFunnel(selectedOffer)}
                  className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-[13px] font-mono py-[14px] rounded-[8px] transition-colors cursor-pointer text-center"
                >
                  ⚡ Start Campaign Wizard →
                </button>
              </div>

            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default OfferScout;
